import React from "react";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";
import styled, { css } from "styled-components";

import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { ITerminology } from "src/library/utils";
import { Text } from "src/library/components/Text";
import Status from "src/library/components/Status/Status";

import { ICallOff } from "../api/call-off.api";

export interface ICallOffColumns {
  actionButton: (info: CellContext<ICallOff, unknown>) => React.ReactNode;
  terminology?: ITerminology,
  excludedColumns?: CallOffAvailableTableColumn[]
  nameHeaderOptions?: Array<{
    text: string;
    value: string;
  }>

}

export type CallOffAvailableTableColumn = keyof AvailableCallOffColumns;

// used to determine what columns can be rendered
interface AvailableCallOffColumns {
  name: ColumnDef<ICallOff, unknown>,
  customerReference: ColumnDef<ICallOff, unknown>,
  customerAccountNumber: ColumnDef<ICallOff, unknown>,
  product: ColumnDef<ICallOff, unknown>,
  status: ColumnDef<ICallOff, unknown>,
  startDate: ColumnDef<ICallOff, unknown>,
  endDate: ColumnDef<ICallOff, unknown>,
  currentQuantity: ColumnDef<ICallOff, unknown>,
  initialQuantity: ColumnDef<ICallOff, unknown>,
  actions: ColumnDef<ICallOff, unknown>
}

export const getCallOffColumns = (params: ICallOffColumns): Array<ColumnDef<ICallOff, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<ICallOff>();
  const columnHelper = createColumnHelper<ICallOff>();

  const allColumns: AvailableCallOffColumns = {
    name: customColumnHelper.standardTextColumn(columnHelper, "name", { header: "Name" }),
    currentQuantity: customColumnHelper.numberColumn(columnHelper, "currentQuantity", { header: "Available Quantity" }),
    customerAccountNumber: customColumnHelper.standardTextColumn(columnHelper, "customer.accountNumber", { header: params.terminology?.customer.singular.text }),
    product: customColumnHelper.standardTextColumn(columnHelper, "product.partNumber", {
      header: params.terminology?.partNumber.singular.text,
      cell: info => {
        return (
          <Text>
            {`${info.cell.row.original.product?.description} (${info.cell.row.original.product?.partNumber})`}
          </Text>
        );
      }
    }),
    customerReference: customColumnHelper.standardTextColumn(columnHelper, "customerReference", { header: "Customer Reference" }),
    status: customColumnHelper.standardTextColumn(columnHelper, "deleted", {
      header: "Status",
      enableColumnFilter: false,
      cell: info => {
        if (info.row.original.deleted) {
          return (
            <Status status="deleted" />
          );
        }

        return (
          <Status status="active" />

        );
      }
    }),
    startDate: customColumnHelper.dateColumn(columnHelper, "startDate", { header: "Start Date" }),
    endDate: customColumnHelper.dateColumn(columnHelper, "endDate", { header: "End Date" }),
    initialQuantity: customColumnHelper.numberColumn(columnHelper, "initialQuantity", { header: "Total Quantity" }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})
  };

  if (params.excludedColumns) {
    const result = allColumns;

    for (const col of params.excludedColumns) {
      delete result[ col ];
    }

    return Object.values(result);
  } else {
    return Object.values(allColumns);
  }
};

interface StatusWrapperProps {
  color: string
}

const StatusWrapper = styled.div<StatusWrapperProps>`
  ${({ theme, color }) => css`
    span {
      color: ${theme.colors[ color ]}
    }
`}
`;
